import { makeStateKey, StateKey, TransferState } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, Observable, of } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

import { BUILD_INFO } from 'src/environments/build-info';
import { idiomasParaTentar } from '../services/idiomas-disponiveis';

/** O idioma padrão do app — o mesmo `DEFAULT_LANGUAGE` do RestaurantService. */
const IDIOMA_PADRAO = 'pt-br';

/**
 * Chave da TransferState com as traduções de um idioma.
 *
 * Existe por causa da hidratação: o `TranslatePipe` devolve `''` até o JSON do
 * idioma chegar, e no cliente ele chega por HTTP, DEPOIS do primeiro render. Com
 * o HTML vindo pronto do servidor, isso faria todo texto fixo da tela (“Aberto
 * agora”, “Buscar”, “Entrega”) sumir no instante da hidratação e voltar em
 * seguida.
 *
 * Desde 23/09/2026 o servidor NÃO grava mais aqui (ver `esperarTraducoesDaPaginaDoServidor`).
 * A leitura continua para as páginas geradas por builds anteriores, que ainda
 * trazem as traduções no estado até serem regeradas.
 */
export function chaveDasTraducoes(idioma: string): StateKey<Record<string, any>> {
    return makeStateKey<Record<string, any>>(`i18n:${idioma}`);
}

/**
 * Caminho do JSON de um idioma, VERSIONADO pelo build.
 *
 * O arquivo tem o mesmo nome em todo deploy e o S3 o entrega com `max-age=3600`: sem
 * a versão, uma tradução alterada levava até 1 hora para chegar a quem já tinha o
 * arquivo guardado. Com `?v=<commit>`, cada deploy muda a URL e o navegador busca o
 * novo na primeira visita; entre deploys, reaproveita o guardado. A borda não
 * precisa disso: o deploy já invalida `/*`, e a query não entra na chave de cache.
 *
 * É a MESMA string no `<link rel="preload">` que o servidor escreve e no pedido do
 * loader do navegador — se divergirem, o navegador baixa duas vezes.
 */
export function caminhoDasTraducoes(idioma: string): string {
    return `assets/i18n/${idioma}.json?v=${encodeURIComponent(BUILD_INFO.release)}`;
}

/**
 * Loader do navegador: usa as traduções embutidas no HTML quando existirem (páginas
 * de builds anteriores) e, fora isso, busca o JSON versionado.
 *
 * Um pedido por idioma de cada vez. Na página do SSR, dois caminhos pedem o mesmo idioma no
 * MESMO instante do boot: o `APP_INITIALIZER` (`esperarTraducoesDaPaginaDoServidor`, que chama
 * `getTranslation()` direto) e o `RestaurantService.setTranslations()` (via `use()`). O
 * ngx-translate só junta pedidos repetidos dentro do `use()`, então saíam dois downloads do mesmo
 * JSON — medido no PageSpeed em 26/09/2026 com o navegador em inglês (`en-us.json` 2×, 12 KB cada;
 * em português o `<link rel="preload">` atendia os dois e escondia). Enquanto o pedido de um
 * idioma está em andamento, quem pedir de novo recebe o MESMO; terminou (ou falhou), a entrada
 * sai, e um pedido depois disso vai à rede — o `reloadLang` continua recarregando.
 */
export class TranslateLoaderComEstadoDoServidor implements TranslateLoader {
    private readonly rede: TranslateHttpLoader;
    private readonly emAndamento = new Map<string, Observable<any>>();

    constructor(http: HttpClient, private readonly estado: TransferState) {
        this.rede = new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${encodeURIComponent(BUILD_INFO.release)}`);
    }

    getTranslation(idioma: string): Observable<any> {
        const chave = chaveDasTraducoes(idioma);
        if (this.estado.hasKey(chave)) {
            const traducoes = this.estado.get(chave, null);
            this.estado.remove(chave);
            if (traducoes) return of(traducoes);
        }
        const existente = this.emAndamento.get(idioma);
        if (existente) return existente;
        const pedido = this.rede.getTranslation(idioma).pipe(
            finalize(() => this.emAndamento.delete(idioma)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.emAndamento.set(idioma, pedido);
        return pedido;
    }
}

/** Teto da espera: tradução lenta pode piscar o texto, mas nunca pode segurar o app. */
export const ESPERA_MAXIMA_DAS_TRADUCOES_MS = 2500;

/**
 * APP_INITIALIZER do navegador: numa página que veio do SSR, carrega as traduções
 * ANTES da hidratação.
 *
 * As traduções saíram do estado da página (eram 35 KB abertos, ~11 KB comprimidos,
 * ~15% do HTML de cada loja, e são iguais para todas). O servidor agora escreve um
 * `<link rel="preload">` para o JSON versionado, então o download começa enquanto o
 * HTML ainda chega e costuma terminar antes do `main.js`. Esperá-lo aqui garante que,
 * quando o RestaurantService chamar `use()`, as traduções já estejam carregadas e o
 * primeiro render saia igual ao do servidor — é o que a TransferState fazia.
 *
 * O idioma é escolhido pela MESMA regra do `setTranslations`: o do navegador primeiro,
 * depois o da loja (que é o que o servidor usou e anotou em `data-mp-idioma`), depois o
 * padrão. Sem SSR (`data-mp-ssr` ausente) não espera nada: o boot do CSR fica como era.
 */
export function esperarTraducoesDaPaginaDoServidor(doc: Document, traducao: TranslateService) {
    return (): Promise<unknown> => {
        if (typeof window === 'undefined' || !doc || !doc.documentElement) return Promise.resolve();
        const html = doc.documentElement;
        if (html.getAttribute('data-mp-ssr') !== '1') return Promise.resolve();
        const navegador = typeof navigator !== 'undefined' ? navigator.language : null;
        const [idioma] = idiomasParaTentar([navegador, html.getAttribute('data-mp-idioma'), IDIOMA_PADRAO]);
        if (!idioma) return Promise.resolve();
        const carregar = firstValueFrom(traducao.getTranslation(idioma)).catch(() => undefined);
        const teto = new Promise((ok) => setTimeout(ok, ESPERA_MAXIMA_DAS_TRADUCOES_MS));
        return Promise.race([carregar, teto]);
    };
}
